import React, { useState, useEffect, useRef, useCallback } from 'react';
import { incluyeTexto } from '../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Mic, Square, Pause, Play, Plus, Search, Clock, Users,
    CheckCircle, ChevronRight, X, Sparkles, Flag,
    Calendar, User, ArrowLeft, Trash2, BookOpen, Shield,
    AlertCircle, RefreshCw, Copy, KanbanSquare
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import AiLoadingOverlay from './AiLoadingOverlay';
import CrearTareaModal from './Proyectos/CrearTareaModal';
import './MeetingRecordingView.css';

// Clave de localStorage que marca una grabacion en curso: si la pagina muere
// (crash, refresh, navegacion), al volver se ofrece recuperar la minuta
const GRABACION_ACTIVA_KEY = 'minuta_grabacion_activa';

// ============================================
// COMPONENTE: Player de audio autenticado
// (descarga via axios con header Authorization; el token no viaja en la URL)
// ============================================
const AudioPlayerAutenticado = ({ minutaId }) => {
    const [src, setSrc] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let objectUrl = null;
        let cancelado = false;
        axiosInstance.get(`/api/minutas/${minutaId}/audio`, { responseType: 'blob', timeout: 0 })
            .then(res => {
                if (cancelado) return;
                objectUrl = URL.createObjectURL(res.data);
                setSrc(objectUrl);
            })
            .catch(() => { if (!cancelado) setError(true); });
        return () => {
            cancelado = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [minutaId]);

    if (error) return <p className="text-sm text-gray-400 italic">Audio no disponible.</p>;
    if (!src) {
        return (
            <div className="h-10 flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full" />
            </div>
        );
    }
    return <audio controls className="w-full" src={src}>Tu navegador no soporta audio.</audio>;
};

// ============================================
// COMPONENTE: Waveform animado estilo WhatsApp
// ============================================
const Waveform = ({ isPlaying, color = '#3b82f6', barCount = 50 }) => {
    const canvasRef = useRef(null);
    const animFrameRef = useRef(null);
    const phaseRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx.scale(dpr, dpr);
        };
        resize();

        const draw = () => {
            const rect = canvas.getBoundingClientRect();
            const w = rect.width;
            const h = rect.height;
            ctx.clearRect(0, 0, w, h);

            const barWidth = 3;
            const gap = (w - barCount * barWidth) / (barCount - 1);
            const centerY = h / 2;

            if (isPlaying) phaseRef.current += 0.06;

            for (let i = 0; i < barCount; i++) {
                const x = i * (barWidth + gap);
                let barH;
                if (isPlaying) {
                    const wave1 = Math.sin(phaseRef.current + i * 0.25) * 0.35;
                    const wave2 = Math.sin(phaseRef.current * 1.7 + i * 0.4) * 0.2;
                    const wave3 = Math.sin(phaseRef.current * 0.5 + i * 0.15) * 0.15;
                    const amplitude = 0.3 + wave1 + wave2 + wave3;
                    barH = Math.max(3, amplitude * h * 0.8);
                } else {
                    barH = 3;
                }
                const y = centerY - barH / 2;
                ctx.fillStyle = color;
                ctx.globalAlpha = isPlaying ? 0.6 + Math.sin(phaseRef.current + i * 0.3) * 0.3 : 0.25;
                ctx.beginPath();
                ctx.roundRect(x, y, barWidth, barH, 1.5);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            animFrameRef.current = requestAnimationFrame(draw);
        };

        animFrameRef.current = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(animFrameRef.current);
    }, [isPlaying, color, barCount]);

    return <canvas ref={canvasRef} className="w-full h-12" style={{ imageRendering: 'auto' }} />;
};

// ============================================
// COMPONENTE: Timer de grabacion
// ============================================
const RecordingTimer = ({ seconds }) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (n) => n.toString().padStart(2, '0');

    return (
        <span className="font-mono text-4xl font-light tracking-wider text-gray-900">
            {h > 0 && <>{pad(h)}:</>}{pad(m)}:{pad(s)}
        </span>
    );
};

// ============================================
// COMPONENTE: Selector de participantes
// ============================================
const ParticipantSelector = ({ selected, onChange }) => {
    const [empleados, setEmpleados] = useState([]);
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        axiosInstance.get('/api/empleados?estado=Activo')
            .then(r => setEmpleados(r.data))
            .catch(() => setEmpleados([]));
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = empleados.filter(e =>
        !selected.find(s => s.id === e.id) &&
        (incluyeTexto(e.nombre, search) || incluyeTexto(e.cargo, search))
    );

    const addParticipant = (emp) => {
        onChange([...selected, emp]);
        setSearch('');
    };

    const removeParticipant = (id) => {
        onChange(selected.filter(p => p.id !== id));
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {selected.map(p => (
                        <span key={p.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-sm">
                            <User size={12} />
                            {p.nombre}
                            <button onClick={() => removeParticipant(p.id)} className="hover:text-blue-900 ml-0.5">
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
                    onFocus={() => setIsOpen(true)}
                    placeholder="Buscar participantes..."
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
            </div>
            <AnimatePresence>
                {isOpen && filtered.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="absolute z-20 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-auto"
                    >
                        {filtered.slice(0, 8).map(emp => (
                            <button
                                key={emp.id}
                                onClick={() => addParticipant(emp)}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                            >
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                    <User size={14} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-800">{emp.nombre}</p>
                                    <p className="text-xs text-gray-400">{emp.cargo}</p>
                                </div>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};


// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const MeetingRecordingView = () => {
    const { user } = useAuth(); // eslint-disable-line no-unused-vars
    const location = useLocation();

    // Estado general
    const [meetings, setMeetings] = useState([]);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [loadingMeetings, setLoadingMeetings] = useState(true);

    // Estado de grabacion
    const [phase, setPhase] = useState('idle'); // idle, setup, recording, processing, done, error
    const [recordingTitle, setRecordingTitle] = useState('');
    const [recordingParticipants, setRecordingParticipants] = useState([]);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [includeSystemAudio, setIncludeSystemAudio] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const startingRef = useRef(false); // evita doble inicio (doble clic = dos grabadores en la misma minuta)
    const timerRef = useRef(null);
    const systemStreamRef = useRef(null);
    const audioContextRef = useRef(null);

    // Estado de processing
    const [processingStep, setProcessingStep] = useState('');
    const [processingProgress, setProcessingProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');

    // MediaRecorder refs
    const mediaRecorderRef = useRef(null);
    const audioStreamRef = useRef(null);
    const chunksRef = useRef([]);
    const chunkIndexRef = useRef(0);
    const chunkIntervalRef = useRef(null);
    const minutaIdRef = useRef(null);
    const pollingRef = useRef(null);
    const phaseRef = useRef('idle');

    // Salud de la grabacion: detectar fallos silenciosos
    const healthCheckRef = useRef(null);
    const lastDataAvailableRef = useRef(0); // timestamp del ultimo chunk de MediaRecorder
    const uploadQueueRef = useRef([]); // chunks pendientes de subida + reintentos
    const uploadInFlightRef = useRef(false);
    const uploadedChunksCountRef = useRef(0); // cuantos chunks confirmados en backend
    const analyserRef = useRef(null);
    const analyserRafRef = useRef(null);

    // Recuperacion automatica del recorder
    const recoveryAttemptsRef = useRef(0);
    const recoveringRef = useRef(false);
    const recoveryBannerRef = useRef(false); // hay banner de recuperacion visible
    const intentarRecuperacionRef = useRef(null); // rompe dependencia circular recorder<->recovery
    const stopRecordingRef = useRef(null);

    // Estado visible al usuario
    const [audioLevel, setAudioLevel] = useState(0); // 0-100, nivel de mic en vivo
    const [pendingUploads, setPendingUploads] = useState(0);
    const [uploadedCount, setUploadedCount] = useState(0);
    const [recorderHealth, setRecorderHealth] = useState('ok'); // ok | warning | failed
    const [healthMessage, setHealthMessage] = useState('');

    // Grabacion interrumpida seleccionada: info de chunks en el servidor
    const [interruptedInfo, setInterruptedInfo] = useState(null);

    // Seccion expandida en resultado
    const [expandedSections, setExpandedSections] = useState({
        resumen: true, participantes: true, timeline: true, tareas: true, secciones: true
    });
    const [resultTab, setResultTab] = useState('analisis'); // analisis | transcripcion

    // Conversión a tarea (Fase 6)
    const [convertModalOpen, setConvertModalOpen] = useState(false);
    const [convertPrefill, setConvertPrefill] = useState({ titulo: '', descripcion: '', prioridad: 'ninguna', fecha: null });

    // Búsqueda cross-meeting
    const [globalSearch, setGlobalSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [aiAnswer, setAiAnswer] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    // Filtro de meetings
    const filteredMeetings = meetings.filter(m =>
        m.titulo.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ---- Cargar minutas desde API ----
    useEffect(() => {
        loadMeetings();
    }, []);

    const loadMeetings = async () => {
        try {
            setLoadingMeetings(true);
            const res = await axiosInstance.get('/api/minutas');
            setMeetings(res.data);
        } catch (err) {
            console.error('Error cargando minutas:', err);
        } finally {
            setLoadingMeetings(false);
        }
    };

    // ---- Timer de grabacion ----
    useEffect(() => {
        if (phase === 'recording' && !isPaused) {
            timerRef.current = setInterval(() => {
                setRecordingSeconds(s => s + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [phase, isPaused]);

    // ---- Limite de 2 horas ----
    useEffect(() => {
        if (phase !== 'recording') return;

        if (recordingSeconds >= 7200) {
            // Auto-stop a las 2 horas
            stopRecording();
        }
    }, [recordingSeconds, phase]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Mantener phaseRef sincronizado ----
    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    // ---- Mantener isPausedRef sincronizado (para closures dentro de intervalos) ----
    const isPausedRef = useRef(false);
    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    // ---- Bloquear interfaz mientras graba ----
    useEffect(() => {
        if (phase !== 'recording') {
            document.body.classList.remove('recording-active');
            return;
        }

        document.body.classList.add('recording-active');

        // Pushear estado para interceptar botón "atrás"
        window.history.pushState({ recording: true }, '');
        const handlePopState = () => {
            if (phaseRef.current === 'recording') {
                window.history.pushState({ recording: true }, '');
            }
        };
        window.addEventListener('popstate', handlePopState);

        return () => {
            document.body.classList.remove('recording-active');
            window.removeEventListener('popstate', handlePopState);
        };
    }, [phase]);

    // ---- Avisar si el usuario intenta cerrar mientras graba ----
    // IMPORTANTE: ya NO se cancela la minuta al salir. Los chunks subidos
    // quedan en el servidor y la grabacion aparece como "Interrumpida",
    // desde donde se puede procesar, reanudar o descartar. Borrar audio
    // automaticamente era la causa de grabaciones perdidas sin rastro.
    useEffect(() => {
        // Bloquear cierre durante recording o uploading_final con warning del browser
        const beforeUnloadHandler = (e) => {
            if (phaseRef.current === 'recording' || phaseRef.current === 'uploading_final') {
                e.preventDefault();
                e.returnValue = 'Hay una grabacion en curso. Si cierras ahora podras recuperarla desde el listado de minutas.';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
            // Cleanup al desmontar (navegacion interna): liberar mic e intervalos
            clearInterval(timerRef.current);
            clearInterval(chunkIntervalRef.current);
            clearInterval(healthCheckRef.current);
            clearInterval(pollingRef.current);
            if (analyserRafRef.current) {
                cancelAnimationFrame(analyserRafRef.current);
                analyserRafRef.current = null;
            }
            analyserRef.current = null;
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(t => t.stop());
            }
            if (systemStreamRef.current) {
                systemStreamRef.current.getTracks().forEach(t => t.stop());
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    // ---- Auto-refresh cuando hay minutas procesándose ----
    // ('grabando' queda fuera: una minuta en ese estado dentro del listado
    // es una grabacion interrumpida, no avanza sola)
    const hasProcessingMeetings = meetings.some(m =>
        ['subido', 'transcribiendo', 'analizando'].includes(m.estado)
    );

    useEffect(() => {
        if (!hasProcessingMeetings || phase === 'recording') return;

        const interval = setInterval(async () => {
            try {
                const res = await axiosInstance.get('/api/minutas');
                setMeetings(prev => {
                    for (const newM of res.data) {
                        const oldM = prev.find(p => p.id === newM.id);
                        if (oldM && ['transcribiendo', 'analizando', 'subido'].includes(oldM.estado)) {
                            if (newM.estado === 'completada') toast.success(`"${newM.titulo}" lista.`, { duration: 5000 });
                            else if (newM.estado === 'error') toast.error(`Error procesando "${newM.titulo}".`);
                        }
                    }
                    return res.data;
                });
            } catch (err) { /* silencioso */ }
        }, 10000);

        return () => clearInterval(interval);
    }, [hasProcessingMeetings, phase]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Búsqueda debounced ----
    useEffect(() => {
        if (!globalSearch.trim()) {
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await axiosInstance.get(`/api/minutas/buscar?q=${encodeURIComponent(globalSearch)}`);
                setSearchResults(res.data);
            } catch (err) {
                console.error('Error buscando:', err);
            } finally {
                setSearchLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [globalSearch]);

    // ---- Helpers ----
    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatDateFull = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        if (m >= 60) {
            const h = Math.floor(m / 60);
            return `${h}h ${m % 60}min`;
        }
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    // eslint-disable-next-line no-unused-vars
    const formatTime = (seconds) => {
        if (seconds < 0) seconds = 0;
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // Parse **bold** markers in text
    const renderBoldText = (text) => {
        if (!text) return null;
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) =>
            part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold text-gray-800">{part.slice(2, -2)}</strong>
                : part
        );
    };

    // ---- Procesar cola de uploads (un chunk a la vez, en orden, con reintentos) ----
    const processUploadQueue = useCallback(async () => {
        if (uploadInFlightRef.current) return;
        if (uploadQueueRef.current.length === 0) return;
        const minutaId = minutaIdRef.current;
        if (!minutaId) return;

        uploadInFlightRef.current = true;

        while (uploadQueueRef.current.length > 0) {
            const item = uploadQueueRef.current[0];
            const { blob, index, attempts } = item;

            const formData = new FormData();
            formData.append('audio', blob, `chunk_${index}.webm`);
            formData.append('chunk_index', index);

            try {
                await axiosInstance.post(`/api/minutas/${minutaId}/chunk`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 60000,
                });
                // Exito: sacar de la cola y contar
                uploadQueueRef.current.shift();
                uploadedChunksCountRef.current += 1;
                setUploadedCount(uploadedChunksCountRef.current);
                setPendingUploads(uploadQueueRef.current.length);
            } catch (err) {
                item.attempts = attempts + 1;
                console.error(`Error subiendo chunk ${index} (intento ${item.attempts}):`, err.message);

                // Backoff: 2s, 5s, 10s, 20s, 30s, despues mantener 30s
                const delays = [2000, 5000, 10000, 20000, 30000];
                const delay = delays[Math.min(item.attempts - 1, delays.length - 1)];

                if (item.attempts >= 3) {
                    setRecorderHealth('warning');
                    setHealthMessage(`Reintentando subir audio (intento ${item.attempts})...`);
                }
                setPendingUploads(uploadQueueRef.current.length);

                // Liberar el lock y reintentar mas tarde
                uploadInFlightRef.current = false;
                setTimeout(() => processUploadQueue(), delay);
                return;
            }
        }

        uploadInFlightRef.current = false;
        // Si limpiamos cola y estabamos en warning por fallos, recuperar
        setHealthMessage(prev => prev.startsWith('Reintentando') ? '' : prev);
        setRecorderHealth(prev => (prev === 'warning' ? 'ok' : prev));
    }, []);

    // ---- Encolar chunk para upload (no descarta nunca) ----
    const enqueueChunk = useCallback((blob, index) => {
        if (blob.size === 0) return;
        uploadQueueRef.current.push({ blob, index, attempts: 0 });
        setPendingUploads(uploadQueueRef.current.length);
        processUploadQueue();
    }, [processUploadQueue]);

    // ---- Flush chunks acumulados y encolar ----
    const flushAndUpload = useCallback(() => {
        if (chunksRef.current.length === 0) return;
        const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
        chunksRef.current = [];
        const idx = chunkIndexRef.current;
        chunkIndexRef.current += 1;
        enqueueChunk(blob, idx);
    }, [enqueueChunk]);

    // ---- VU meter sobre un stream (se reconecta tras recuperacion) ----
    const conectarVuMeter = useCallback((stream) => {
        try {
            if (analyserRafRef.current) cancelAnimationFrame(analyserRafRef.current);
            const ctx = audioContextRef.current && audioContextRef.current.state !== 'closed'
                ? audioContextRef.current
                : new AudioContext();
            audioContextRef.current = ctx;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            ctx.createMediaStreamSource(stream).connect(analyser);
            analyserRef.current = analyser;

            const buf = new Uint8Array(analyser.frequencyBinCount);
            const sampleLevel = () => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteTimeDomainData(buf);
                // RMS aproximado
                let sum = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = (buf[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / buf.length);
                setAudioLevel(Math.min(100, Math.round(rms * 200)));
                analyserRafRef.current = requestAnimationFrame(sampleLevel);
            };
            sampleLevel();
        } catch (analyserErr) {
            console.warn('No se pudo crear analyser para VU meter:', analyserErr.message);
        }
    }, []);

    // ---- Crear y arrancar un MediaRecorder sobre un stream ----
    // (usado al iniciar, al reanudar y en la recuperacion automatica)
    const crearYArrancarRecorder = useCallback((stream) => {
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm';

        const recorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            lastDataAvailableRef.current = Date.now();
            recoveryAttemptsRef.current = 0; // llego data: la captura esta sana
            if (recoveryBannerRef.current) {
                // La recuperacion funciono: limpiar el banner
                recoveryBannerRef.current = false;
                setRecorderHealth('ok');
                setHealthMessage('');
            }
            if (e.data && e.data.size > 0) {
                chunksRef.current.push(e.data);
            }
        };

        recorder.onerror = (e) => {
            console.error('MediaRecorder error:', e);
            intentarRecuperacionRef.current?.();
        };

        recorder.start(5000); // blobs cada 5s
        lastDataAvailableRef.current = Date.now();
    }, []);

    // ---- Detener y procesar lo capturado cuando la recuperacion no alcanza ----
    const detenerYProcesarAutomatico = useCallback((motivo) => {
        setRecorderHealth('failed');
        setHealthMessage(`${motivo} Se detiene la grabacion para no perder lo capturado.`);
        toast.error(`${motivo} Se detiene la grabacion y se procesa lo capturado.`, { duration: 10000 });
        // Notificacion del sistema: el caso tipico es que nadie esta mirando la pestana
        if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
            try {
                new Notification('Minutas — Centro Sura', {
                    body: `${motivo} Se detuvo la grabacion y se procesa el audio capturado.`,
                });
            } catch (e) { /* noop */ }
        }
        stopRecordingRef.current?.();
    }, []);

    // ---- Recuperacion automatica del recorder ----
    // Si MediaRecorder muere en silencio (sleep del sistema, mic desconectado,
    // error interno) se reinicia solo. Tras 2 intentos fallidos se detiene y
    // procesa lo que haya: un banner que nadie ve no salva ninguna reunion.
    const intentarRecuperacion = useCallback(async () => {
        if (recoveringRef.current || phaseRef.current !== 'recording' || isPausedRef.current) return;
        recoveringRef.current = true;
        recoveryAttemptsRef.current += 1;

        if (recoveryAttemptsRef.current > 2) {
            recoveringRef.current = false;
            detenerYProcesarAutomatico('El grabador fallo varias veces seguidas.');
            return;
        }

        setRecorderHealth('warning');
        setHealthMessage('El grabador dejo de capturar audio. Reiniciando automaticamente...');
        recoveryBannerRef.current = true;

        try {
            const viejo = mediaRecorderRef.current;
            if (viejo && viejo.state !== 'inactive') {
                await new Promise((resolve) => {
                    viejo.onstop = resolve;
                    try { viejo.stop(); } catch (e) { resolve(); }
                    setTimeout(resolve, 3000); // por si onstop nunca dispara
                });
            }
            // Subir lo pendiente del stream anterior ANTES de abrir uno nuevo:
            // el orden de los chunks debe respetar los segmentos WebM
            flushAndUpload();

            let stream = audioStreamRef.current;
            const trackVivo = stream && stream.getAudioTracks().some(t => t.readyState === 'live');
            if (!trackVivo) {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                if (audioStreamRef.current) {
                    audioStreamRef.current.getTracks().forEach(t => t.stop());
                }
                audioStreamRef.current = stream;
            }

            // Nota: si se grababa con audio del sistema, la recuperacion
            // continua solo con el microfono
            conectarVuMeter(stream);
            crearYArrancarRecorder(stream);
            setHealthMessage('Grabador reiniciado automaticamente. Verificando captura...');
        } catch (err) {
            console.error('Recuperacion del grabador fallo:', err);
            detenerYProcesarAutomatico('No se pudo recuperar el microfono.');
        } finally {
            recoveringRef.current = false;
        }
    }, [crearYArrancarRecorder, conectarVuMeter, flushAndUpload, detenerYProcesarAutomatico]);

    useEffect(() => {
        intentarRecuperacionRef.current = intentarRecuperacion;
    }, [intentarRecuperacion]);

    // ---- Intervalos de flush y health check ----
    const iniciarIntervalos = useCallback(() => {
        clearInterval(chunkIntervalRef.current);
        clearInterval(healthCheckRef.current);

        // Upload cada 15s: acota la perdida maxima ante crash de pestana/sistema
        chunkIntervalRef.current = setInterval(() => {
            flushAndUpload();
        }, 15000);

        // Health check cada 10s: si el recorder murio en silencio, recuperar
        healthCheckRef.current = setInterval(() => {
            if (phaseRef.current !== 'recording' || isPausedRef.current) return;
            const r = mediaRecorderRef.current;
            const sinceLastChunk = Date.now() - lastDataAvailableRef.current;
            const murio = sinceLastChunk > 20000
                || (r && r.state !== 'recording' && r.state !== 'paused');
            if (murio) intentarRecuperacionRef.current?.();
        }, 10000);
    }, [flushAndUpload]);

    // ---- Acciones ----
    const startSetup = useCallback(() => {
        setPhase('setup');
        setRecordingTitle('');
        // Pre-cargar al usuario actual como participante
        const initialParticipants = user?.empleado_id
            ? [{ id: user.empleado_id, nombre: user.nombre_empleado }]
            : [];
        setRecordingParticipants(initialParticipants);
        setRecordingSeconds(0);
        setSelectedMeeting(null);
        setErrorMessage('');
    }, [user]);

    const startRecording = useCallback(async () => {
        if (!recordingTitle.trim() || startingRef.current) return;
        startingRef.current = true;
        setIsStarting(true);

        let micStream = null;
        let systemStream = null;

        // Reset estado de salud y contadores
        uploadQueueRef.current = [];
        uploadedChunksCountRef.current = 0;
        recoveryAttemptsRef.current = 0;
        lastDataAvailableRef.current = Date.now();
        setUploadedCount(0);
        setPendingUploads(0);
        setRecorderHealth('ok');
        setHealthMessage('');
        setAudioLevel(0);

        // Permiso para notificar fallos cuando la pestana esta en segundo plano
        if ('Notification' in window && Notification.permission === 'default') {
            try { Notification.requestPermission().catch(() => {}); } catch (e) { /* noop */ }
        }

        try {
            // 1. Pedir mic ANTES de crear la minuta (si falla, no dejamos fila huerfana)
            micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // 2. Si incluye audio del sistema, pedirlo tambien antes de tocar el backend
            if (includeSystemAudio) {
                try {
                    systemStream = await navigator.mediaDevices.getDisplayMedia({
                        video: true,
                        audio: true,
                    });
                } catch (displayErr) {
                    console.warn('No se pudo obtener audio del sistema, continuando solo con mic:', displayErr.message);
                    systemStream = null;
                }
            }

            // 3. Crear minuta en backend (solo tras confirmar permisos)
            const res = await axiosInstance.post('/api/minutas', {
                titulo: recordingTitle.trim(),
                fecha: new Date().toISOString(),
                participantes: recordingParticipants.map(p => p.id)
            });
            const minuta = res.data;
            minutaIdRef.current = minuta.id;

            audioStreamRef.current = micStream;
            let finalStream = micStream;

            // 3b. Detectar desconexion del mic (USB/Bluetooth) — causa principal de grabaciones rotas
            const micTrack = micStream.getAudioTracks()[0];
            if (micTrack) {
                micTrack.onended = () => {
                    if (phaseRef.current === 'recording') {
                        toast.error('Microfono desconectado, intentando recuperar...', { duration: 6000 });
                        intentarRecuperacionRef.current?.();
                    }
                };
                micTrack.onmute = () => {
                    if (phaseRef.current === 'recording') {
                        setRecorderHealth('warning');
                        setHealthMessage('El microfono fue silenciado.');
                    }
                };
                micTrack.onunmute = () => {
                    if (phaseRef.current === 'recording') {
                        setHealthMessage(prev => prev.includes('silenciado') ? '' : prev);
                        setRecorderHealth(prev => prev === 'warning' ? 'ok' : prev);
                    }
                };
            }

            // 4. Mezclar mic + sistema si corresponde
            if (systemStream) {
                try {
                    systemStreamRef.current = systemStream;

                    // Detener el track de video (solo nos interesa el audio)
                    systemStream.getVideoTracks().forEach(t => t.stop());

                    const systemAudioTrack = systemStream.getAudioTracks()[0];
                    if (systemAudioTrack) {
                        // Mezclar ambos streams con AudioContext
                        const ctx = new AudioContext();
                        audioContextRef.current = ctx;
                        const dest = ctx.createMediaStreamDestination();

                        const micSource = ctx.createMediaStreamSource(micStream);
                        micSource.connect(dest);

                        const sysSource = ctx.createMediaStreamSource(new MediaStream([systemAudioTrack]));
                        sysSource.connect(dest);

                        finalStream = dest.stream;

                        // Si el usuario deja de compartir pantalla, seguir solo con mic
                        systemAudioTrack.onended = () => {
                            console.log('Audio del sistema detenido, continuando solo con mic');
                            systemStreamRef.current = null;
                        };
                    }
                } catch (displayErr) {
                    // Si cancela el diálogo de compartir pantalla, continuar solo con mic
                    console.warn('No se pudo obtener audio del sistema, continuando solo con mic:', displayErr.message);
                }
            }

            // 4b. VU meter sobre el stream final
            conectarVuMeter(finalStream);

            // 5. Crear MediaRecorder con el stream final (mic o mix)
            chunksRef.current = [];
            chunkIndexRef.current = 0;
            crearYArrancarRecorder(finalStream);

            // 6. Flush periodico + health check con recuperacion automatica
            iniciarIntervalos();

            // Marcar grabacion activa: si la pagina muere, al volver se recupera
            try {
                localStorage.setItem(GRABACION_ACTIVA_KEY, JSON.stringify({ id: minuta.id, ts: Date.now() }));
            } catch (e) { /* noop */ }

            setPhase('recording');
            setIsPaused(false);

        } catch (err) {
            console.error('Error al iniciar grabacion:', err);

            // Liberar streams si se obtuvieron antes del error
            if (micStream) micStream.getTracks().forEach(t => t.stop());
            if (systemStream) systemStream.getTracks().forEach(t => t.stop());

            // Si la minuta ya se creo en backend, cancelarla para no dejar fila huerfana
            if (minutaIdRef.current) {
                axiosInstance.post(`/api/minutas/${minutaIdRef.current}/cancelar`).catch(() => {});
                minutaIdRef.current = null;
            }

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setErrorMessage('Permiso de microfono denegado. Por favor habilita el acceso al microfono en tu navegador.');
            } else if (err.response) {
                setErrorMessage(err.response.data?.error || 'Error creando la minuta en el servidor.');
            } else {
                setErrorMessage('Error inesperado al iniciar la grabacion.');
            }
            setPhase('error');
        } finally {
            startingRef.current = false;
            setIsStarting(false);
        }
    }, [recordingTitle, recordingParticipants, includeSystemAudio, conectarVuMeter, crearYArrancarRecorder, iniciarIntervalos]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePause = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (!recorder) return;

        if (isPaused) {
            recorder.resume();
            // Resetear timestamp para que el health check no marque falso positivo
            lastDataAvailableRef.current = Date.now();
            setIsPaused(false);
        } else {
            recorder.pause();
            setIsPaused(true);
        }
    }, [isPaused]);

    const stopRecording = useCallback(async () => {
        const recorder = mediaRecorderRef.current;
        const minutaId = minutaIdRef.current;

        // Detener intervalos
        clearInterval(chunkIntervalRef.current);
        clearInterval(healthCheckRef.current);

        // Detener VU meter
        if (analyserRafRef.current) {
            cancelAnimationFrame(analyserRafRef.current);
            analyserRafRef.current = null;
        }
        analyserRef.current = null;

        if (recorder && recorder.state !== 'inactive') {
            // Esperar a que el recorder se detenga y obtener los datos finales
            await new Promise((resolve) => {
                recorder.onstop = resolve;
                try {
                    recorder.stop();
                } catch (e) {
                    console.error('Error deteniendo recorder:', e);
                    resolve();
                }
            });
        }

        // Encolar ultimo chunk pendiente (la cola se encarga de subir con reintentos)
        if (chunksRef.current.length > 0) {
            const finalBlob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
            chunksRef.current = [];
            const idx = chunkIndexRef.current;
            chunkIndexRef.current += 1;
            enqueueChunk(finalBlob, idx);
        }

        // Mostrar progreso de upload final al usuario
        setPhase('uploading_final');

        // Esperar a que la cola termine. Mientras haya avance no hay tope de
        // tiempo; solo se abandona si pasan 90s sin subir ni un chunk.
        let ultimoAvance = Date.now();
        let ultimoSubidos = uploadedChunksCountRef.current;
        while (uploadQueueRef.current.length > 0 && Date.now() - ultimoAvance < 90000) {
            await new Promise(r => setTimeout(r, 500));
            if (uploadedChunksCountRef.current !== ultimoSubidos) {
                ultimoSubidos = uploadedChunksCountRef.current;
                ultimoAvance = Date.now();
            }
        }

        const pendientes = uploadQueueRef.current.length;
        const subidos = uploadedChunksCountRef.current;

        // Vaciar la cola: tras finalizar, el backend rechaza chunks nuevos y
        // los reintentos quedarian en un loop infinito de errores 400
        uploadQueueRef.current = [];
        setPendingUploads(0);

        // Detener tracks de audio
        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(t => t.stop());
            audioStreamRef.current = null;
        }
        if (systemStreamRef.current) {
            systemStreamRef.current.getTracks().forEach(t => t.stop());
            systemStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        // Si no se subio NADA, abortar y avisar al usuario
        if (subidos === 0) {
            console.error('Finalizar abortado: ningun chunk fue subido');
            setErrorMessage(
                'No se pudo subir ningun fragmento de audio al servidor. ' +
                'Revisa tu conexion a internet. La grabacion quedo como ' +
                'interrumpida en el listado por si quieres reintentarla.'
            );
            setPhase('error');
            loadMeetings();
            return;
        }

        // Si quedan chunks colgados, avisar pero seguir (mejor algo que nada)
        if (pendientes > 0) {
            toast.error(
                `Atencion: ${pendientes} fragmento(s) de audio no se subieron. ` +
                `La minuta se procesara con lo disponible (${subidos} fragmento(s)).`,
                { duration: 10000 }
            );
        }

        // Notificar al backend — el procesamiento corre en segundo plano (Celery)
        try {
            await axiosInstance.post(`/api/minutas/${minutaId}/finalizar`, {
                duracion_segundos: recordingSeconds
            });

            try { localStorage.removeItem(GRABACION_ACTIVA_KEY); } catch (e) { /* noop */ }

            setMeetings(prev => [{
                id: minutaId,
                titulo: recordingTitle,
                fecha: new Date().toISOString(),
                estado: 'transcribiendo',
                duracion_segundos: recordingSeconds,
                participantes: recordingParticipants,
            }, ...prev]);

            setPhase('idle');
            setSelectedMeeting(null);
            toast.success(`Grabación finalizada (${subidos} fragmentos). Se procesara en segundo plano.`, { duration: 4000 });
        } catch (err) {
            console.error('Error finalizando minuta:', err);
            const backendMsg = err.response?.data?.error;
            // La minuta sigue en 'grabando' con su audio en el servidor:
            // aparece como interrumpida y se puede procesar desde alli
            setErrorMessage(backendMsg || 'Error al finalizar la grabacion. Quedo como interrumpida en el listado: desde alli puedes procesarla.');
            setPhase('error');
            loadMeetings();
        }
    }, [recordingSeconds, recordingTitle, recordingParticipants, enqueueChunk]); // eslint-disable-line react-hooks/exhaustive-deps

    // Ref para que la recuperacion automatica pueda detener la grabacion
    useEffect(() => {
        stopRecordingRef.current = stopRecording;
    }, [stopRecording]);

    // ---- Polling de estado del procesamiento ----
    const startStatusPolling = useCallback((minutaId) => {
        clearInterval(pollingRef.current);

        pollingRef.current = setInterval(async () => {
            try {
                const res = await axiosInstance.get(`/api/minutas/${minutaId}/status`);
                const { estado, paso_actual, porcentaje, error_mensaje } = res.data;

                setProcessingStep(paso_actual || 'Procesando...');
                setProcessingProgress(porcentaje || 0);

                if (estado === 'completada') {
                    clearInterval(pollingRef.current);
                    // Cargar minuta completa
                    const fullRes = await axiosInstance.get(`/api/minutas/${minutaId}`);
                    const fullMinuta = fullRes.data;
                    // Actualizar en la lista
                    setMeetings(prev => {
                        const exists = prev.find(m => m.id === fullMinuta.id);
                        if (exists) {
                            return prev.map(m => m.id === fullMinuta.id ? fullMinuta : m);
                        }
                        return [fullMinuta, ...prev];
                    });
                    setSelectedMeeting(fullMinuta);
                    setPhase('done');
                } else if (estado === 'error') {
                    clearInterval(pollingRef.current);
                    setErrorMessage(error_mensaje || 'Error durante el procesamiento de la minuta.');
                    // Guardar el id para poder reprocesar
                    setSelectedMeeting({ id: minutaId });
                    setPhase('error');
                }
            } catch (err) {
                console.error('Error en polling de estado:', err);
                // No detener polling por errores de red transitorios
            }
        }, 3000);
    }, []);

    const reprocess = useCallback(async () => {
        const minutaId = minutaIdRef.current || selectedMeeting?.id;
        if (!minutaId) return;

        try {
            await axiosInstance.post(`/api/minutas/${minutaId}/reprocesar`);
            setPhase('processing');
            setProcessingStep('Re-procesando minuta...');
            setProcessingProgress(0);
            setErrorMessage('');
            startStatusPolling(minutaId);
        } catch (err) {
            console.error('Error reprocesando:', err);
            // 409 = aun esta procesando/en cola; mostrar el motivo del backend
            toast.error(err.response?.data?.error || 'No se pudo iniciar el reprocesamiento.');
        }
    }, [selectedMeeting, startStatusPolling]);

    const backToList = useCallback(() => {
        setPhase('idle');
        setSelectedMeeting(null);
        setErrorMessage('');
        setInterruptedInfo(null);
        clearInterval(pollingRef.current);
    }, []);

    const deleteMeeting = useCallback(async (id) => {
        try {
            await axiosInstance.delete(`/api/minutas/${id}`);
            setMeetings(prev => prev.filter(m => m.id !== id));
            if (selectedMeeting?.id === id) {
                setSelectedMeeting(null);
                setPhase('idle');
            }
        } catch (err) {
            console.error('Error eliminando minuta:', err);
        }
    }, [selectedMeeting]);

    const selectMeeting = useCallback((meeting) => {
        if (meeting.estado === 'grabando') {
            // Grabacion interrumpida (crash, refresh, navegacion): ofrecer recuperarla
            minutaIdRef.current = meeting.id;
            setSelectedMeeting(meeting);
            setInterruptedInfo(null);
            setPhase('interrupted');
            axiosInstance.get(`/api/minutas/${meeting.id}/status`)
                .then(res => setInterruptedInfo(res.data))
                .catch(() => setInterruptedInfo({ chunks_subidos: null, ultimo_chunk: -1 }));
        } else if (meeting.estado === 'subido' || meeting.estado === 'transcribiendo' || meeting.estado === 'analizando') {
            // Si la minuta esta en proceso, reanudar polling
            minutaIdRef.current = meeting.id;
            setSelectedMeeting(meeting);
            setPhase('processing');
            setProcessingStep('Procesando...');
            setProcessingProgress(0);
            startStatusPolling(meeting.id);
        } else if (meeting.estado === 'error') {
            minutaIdRef.current = meeting.id;
            setSelectedMeeting(meeting);
            setErrorMessage(meeting.error_mensaje || 'Error durante el procesamiento.');
            setPhase('error');
        } else if (meeting.estado === 'completada') {
            // Cargar detalle completo (con resultado y transcripción)
            setPhase('done');
            axiosInstance.get(`/api/minutas/${meeting.id}`)
                .then(res => setSelectedMeeting(res.data))
                .catch(() => setSelectedMeeting(meeting));
        }
    }, [startStatusPolling]);

    useEffect(() => {
        const minutaId = Number(new URLSearchParams(location.search).get('minuta'));
        if (!minutaId || selectedMeeting?.id === minutaId) return;

        const meeting = meetings.find(m => m.id === minutaId);
        if (meeting) {
            selectMeeting(meeting);
            return;
        }

        axiosInstance.get(`/api/minutas/${minutaId}`)
            .then(res => selectMeeting(res.data))
            .catch(() => {});
    }, [location.search, meetings, selectedMeeting?.id, selectMeeting]);

    // ---- Al montar: detectar grabacion interrumpida (la pestana murio grabando) ----
    useEffect(() => {
        let marker = null;
        try { marker = JSON.parse(localStorage.getItem(GRABACION_ACTIVA_KEY) || 'null'); } catch (e) { marker = null; }
        if (!marker?.id) return;

        axiosInstance.get(`/api/minutas/${marker.id}`)
            .then(res => {
                if (res.data.estado === 'grabando') {
                    selectMeeting(res.data);
                } else {
                    // Ya se proceso o se descarto: limpiar marcador
                    try { localStorage.removeItem(GRABACION_ACTIVA_KEY); } catch (e) { /* noop */ }
                }
            })
            .catch(() => {
                try { localStorage.removeItem(GRABACION_ACTIVA_KEY); } catch (e) { /* noop */ }
            });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Acciones sobre una grabacion interrumpida ----
    const procesarInterrumpida = useCallback(async () => {
        const meeting = selectedMeeting;
        if (!meeting) return;
        try {
            await axiosInstance.post(`/api/minutas/${meeting.id}/finalizar`, {});
            try { localStorage.removeItem(GRABACION_ACTIVA_KEY); } catch (e) { /* noop */ }
            minutaIdRef.current = meeting.id;
            setPhase('processing');
            setProcessingStep('Procesando audio recuperado...');
            setProcessingProgress(0);
            startStatusPolling(meeting.id);
            loadMeetings();
        } catch (err) {
            console.error('Error procesando grabacion interrumpida:', err);
            toast.error(err.response?.data?.error || 'No se pudo procesar la grabacion.');
            if (err.response?.status === 400) {
                // Sin audio utilizable en el servidor: solo queda descartar
                setInterruptedInfo(prev => ({ ...(prev || {}), sin_audio: true }));
            }
        }
    }, [selectedMeeting, startStatusPolling]); // eslint-disable-line react-hooks/exhaustive-deps

    const descartarInterrumpida = useCallback(async () => {
        const meeting = selectedMeeting;
        if (!meeting) return;
        if (!window.confirm('Se eliminara la grabacion interrumpida y su audio. ¿Continuar?')) return;
        try {
            await axiosInstance.delete(`/api/minutas/${meeting.id}`);
            try { localStorage.removeItem(GRABACION_ACTIVA_KEY); } catch (e) { /* noop */ }
            setMeetings(prev => prev.filter(m => m.id !== meeting.id));
            setSelectedMeeting(null);
            setInterruptedInfo(null);
            setPhase('idle');
            toast.success('Grabacion descartada.');
        } catch (err) {
            console.error('Error descartando grabacion:', err);
            toast.error('No se pudo descartar la grabacion.');
        }
    }, [selectedMeeting]);

    const reanudarGrabacion = useCallback(async () => {
        const meeting = selectedMeeting;
        if (!meeting) return;
        try {
            // Indice del proximo chunk segun lo que realmente llego al servidor
            const st = await axiosInstance.get(`/api/minutas/${meeting.id}/status`);
            const ultimo = st.data.ultimo_chunk ?? -1;

            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            minutaIdRef.current = meeting.id;
            chunkIndexRef.current = ultimo + 1;
            chunksRef.current = [];
            uploadQueueRef.current = [];
            uploadedChunksCountRef.current = st.data.chunks_subidos || 0;
            recoveryAttemptsRef.current = 0;
            setUploadedCount(st.data.chunks_subidos || 0);
            setPendingUploads(0);
            setRecorderHealth('ok');
            setHealthMessage('');
            setAudioLevel(0);
            setRecordingTitle(meeting.titulo);
            setRecordingParticipants(meeting.participantes || []);
            // Aproximado: la duracion real la calcula el backend (ffprobe) al procesar
            setRecordingSeconds((ultimo + 1) * 15);

            audioStreamRef.current = micStream;
            const micTrack = micStream.getAudioTracks()[0];
            if (micTrack) {
                micTrack.onended = () => {
                    if (phaseRef.current === 'recording') {
                        toast.error('Microfono desconectado, intentando recuperar...', { duration: 6000 });
                        intentarRecuperacionRef.current?.();
                    }
                };
            }

            conectarVuMeter(micStream);
            crearYArrancarRecorder(micStream);
            iniciarIntervalos();

            try {
                localStorage.setItem(GRABACION_ACTIVA_KEY, JSON.stringify({ id: meeting.id, ts: Date.now() }));
            } catch (e) { /* noop */ }

            setInterruptedInfo(null);
            setPhase('recording');
            setIsPaused(false);
            toast.success('Grabacion reanudada. El audio nuevo se anexa a lo ya guardado.');
        } catch (err) {
            console.error('Error reanudando grabacion:', err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                toast.error('Permiso de microfono denegado.');
            } else {
                toast.error('No se pudo reanudar la grabacion.');
            }
        }
    }, [selectedMeeting, conectarVuMeter, crearYArrancarRecorder, iniciarIntervalos]);

    const copyToClipboard = useCallback((text) => {
        navigator.clipboard.writeText(text).catch(() => {});
    }, []);

    const askAI = useCallback(async () => {
        if (!globalSearch.trim()) return;
        setAiLoading(true);
        setAiAnswer(null);
        try {
            const res = await axiosInstance.post('/api/minutas/preguntar', {
                pregunta: globalSearch,
            });
            setAiAnswer(res.data);
        } catch (err) {
            console.error('Error preguntando a IA:', err);
            toast.error('Error al consultar la IA.');
        } finally {
            setAiLoading(false);
        }
    }, [globalSearch]);

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Badge de estado para sidebar
    const getStatusBadge = (estado) => {
        const badges = {
            // En el listado, 'grabando' = la grabacion se interrumpio (la activa no aparece aqui)
            grabando: { label: 'Interrumpida', className: 'bg-amber-50 text-amber-600 border-amber-200' },
            subido: { label: 'Procesando', className: 'bg-amber-50 text-amber-600 border-amber-200' },
            transcribiendo: { label: 'Transcribiendo', className: 'bg-amber-50 text-amber-600 border-amber-200' },
            analizando: { label: 'Analizando', className: 'bg-blue-50 text-blue-600 border-blue-200' },
            completada: { label: 'Lista', className: 'bg-green-50 text-green-600 border-green-200' },
            error: { label: 'Error', className: 'bg-red-50 text-red-600 border-red-200' },
        };
        const badge = badges[estado] || badges.completada;
        return (
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap border ${badge.className}`}>
                {badge.label}
            </span>
        );
    };

    // ============================================
    // RENDER: Panel izquierdo - Historial (flotante)
    // ============================================
    const renderSidebar = () => (
        <aside className="meeting-sidebar">
            <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Mic size={16} className="text-blue-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">Minutas</h2>
                    </div>
                    <button
                        onClick={startSetup}
                        className="w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors shadow-sm"
                    >
                        <Plus size={16} />
                    </button>
                </div>
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar minuta..."
                        className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
            </div>

            <div className="meeting-sidebar-scroll">
                {loadingMeetings ? (
                    <div className="text-center py-12 px-6">
                        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
                        <p className="text-sm text-gray-400">Cargando minutas...</p>
                    </div>
                ) : filteredMeetings.length === 0 ? (
                    <div className="text-center py-12 px-6">
                        <BookOpen size={32} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-400">No hay minutas registradas</p>
                    </div>
                ) : (
                    <div className="py-2">
                        {filteredMeetings.map((meeting) => (
                            <button
                                key={meeting.id}
                                onClick={() => selectMeeting(meeting)}
                                className={`w-full text-left px-5 py-3.5 transition-colors hover:bg-gray-50 ${
                                    selectedMeeting?.id === meeting.id ? 'bg-blue-50' : ''
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="text-sm font-medium text-gray-900 line-clamp-1">{meeting.titulo}</h3>
                                    {getStatusBadge(meeting.estado)}
                                </div>
                                <div className="flex items-center gap-3 mt-1.5">
                                    <span className="flex items-center gap-1 text-xs text-gray-400">
                                        <Calendar size={11} />
                                        {formatDate(meeting.fecha)}
                                    </span>
                                    {meeting.duracion_segundos > 0 && (
                                        <span className="flex items-center gap-1 text-xs text-gray-400">
                                            <Clock size={11} />
                                            {formatDuration(meeting.duracion_segundos)}
                                        </span>
                                    )}
                                </div>
                                {meeting.participantes && meeting.participantes.length > 0 && (
                                    <div className="flex items-center gap-1 mt-2">
                                        {meeting.participantes.slice(0, 3).map((p, i) => (
                                            <div
                                                key={i}
                                                className="w-6 h-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[9px] font-medium text-gray-500 -ml-1 first:ml-0"
                                                title={p.nombre}
                                            >
                                                {p.nombre?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </div>
                                        ))}
                                        {meeting.participantes.length > 3 && (
                                            <span className="text-xs text-gray-400 ml-1">
                                                +{meeting.participantes.length - 3}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </aside>
    );

    // ============================================
    // RENDER: Empty state + búsqueda cross-meeting
    // ============================================
    const renderEmpty = () => (
        <div className="meeting-panel">
            <div className="max-w-3xl mx-auto px-6 pt-8">
                {/* Búsqueda cross-meeting */}
                <div className="relative mb-6">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={globalSearch}
                        onChange={(e) => { setGlobalSearch(e.target.value); setAiAnswer(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && globalSearch.trim() && askAI()}
                        placeholder="Busca en tus minutas o haz una pregunta..."
                        className="w-full pl-12 pr-36 py-4 bg-white border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                    />
                    {globalSearch.trim() && (
                        <button
                            onClick={askAI}
                            disabled={aiLoading}
                            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-xl text-xs font-medium transition-colors"
                        >
                            <Sparkles size={12} />
                            Preguntar a IA
                        </button>
                    )}
                </div>

                {/* Respuesta IA */}
                {aiLoading && (
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="animate-spin w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full" />
                            <span className="text-sm text-violet-600">Analizando tus minutas...</span>
                        </div>
                    </div>
                )}

                {aiAnswer && !aiLoading && (
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={16} className="text-violet-500" />
                            <span className="text-sm font-semibold text-violet-800">Respuesta de IA</span>
                        </div>
                        <div className="text-sm text-gray-700 leading-relaxed mb-4">
                            {renderBoldText(aiAnswer.respuesta)}
                        </div>
                        {aiAnswer.minutas_referenciadas?.length > 0 && (
                            <div className="border-t border-violet-200 pt-3">
                                <p className="text-[10px] uppercase font-semibold text-violet-400 mb-2">Minutas referenciadas</p>
                                <div className="space-y-1.5">
                                    {aiAnswer.minutas_referenciadas.map((ref, i) => (
                                        <button
                                            key={i}
                                            onClick={() => ref.id && selectMeeting({ id: ref.id, estado: 'completada' })}
                                            className="flex items-center gap-2 text-sm text-violet-700 hover:text-violet-900 transition-colors"
                                        >
                                            <BookOpen size={12} />
                                            <span className="font-medium">{ref.titulo}</span>
                                            <span className="text-violet-400 text-xs">{ref.fecha}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Resultados de búsqueda */}
                {searchResults.length > 0 && !aiLoading && (
                    <div className="space-y-3 mb-8">
                        <p className="text-xs font-medium text-gray-400 uppercase">
                            {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}
                        </p>
                        {searchResults.map((result) => (
                            <button
                                key={result.id}
                                onClick={() => selectMeeting({ id: result.id, estado: 'completada' })}
                                className="w-full text-left p-4 bg-white border border-gray-100 rounded-xl hover:border-blue-200 hover:shadow-sm transition-all"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-semibold text-gray-900">{result.titulo}</h4>
                                    <span className="text-xs text-gray-400">{formatDate(result.fecha)}</span>
                                </div>
                                {result.snippet && (
                                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{result.snippet}</p>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {searchLoading && (
                    <div className="text-center py-8">
                        <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
                    </div>
                )}

                {/* Estado vacío original */}
                {!globalSearch && !aiAnswer && (
                    <div className="text-center pt-16">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center mx-auto mb-6">
                            <Mic size={32} className="text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Minutas Inteligentes</h3>
                        <p className="text-sm text-gray-400 mb-8 leading-relaxed">
                            Graba tus reuniones y obtiene un resumen automatico con tareas, decisiones y proximos pasos generados con IA.
                        </p>
                        <button
                            onClick={startSetup}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
                        >
                            <Mic size={16} />
                            Iniciar grabacion
                        </button>
                    </div>
                )}

                {globalSearch && searchResults.length === 0 && !searchLoading && !aiAnswer && !aiLoading && (
                    <div className="text-center pt-8">
                        <Search size={32} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-400">No se encontraron resultados. Prueba preguntarle a la IA.</p>
                    </div>
                )}
            </div>
        </div>
    );

    // ============================================
    // RENDER: Setup (titulo + participantes)
    // ============================================
    const renderSetup = () => (
        <div className="meeting-panel meeting-panel--centered">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="meeting-panel__content meeting-panel__content--narrow"
            >
                <button onClick={backToList} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors">
                    <ArrowLeft size={14} /> Todas las minutas
                </button>

                <h2 className="text-2xl font-semibold text-gray-900 mb-1">Nueva minuta</h2>
                <p className="text-sm text-gray-400 mb-8">Configura los detalles antes de comenzar la grabacion</p>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Titulo de la minuta</label>
                        <input
                            type="text"
                            value={recordingTitle}
                            onChange={(e) => setRecordingTitle(e.target.value)}
                            placeholder="Ej: Revision semanal de proyectos"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Participantes</label>
                        <ParticipantSelector
                            selected={recordingParticipants}
                            onChange={setRecordingParticipants}
                        />
                    </div>

                    {/* Toggle audio del sistema */}
                    <div
                        onClick={() => setIncludeSystemAudio(!includeSystemAudio)}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                            includeSystemAudio
                                ? 'bg-blue-50 border-blue-200'
                                : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        <div className={`w-10 h-6 rounded-full p-0.5 transition-colors ${includeSystemAudio ? 'bg-blue-500' : 'bg-gray-300'}`}>
                            <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${includeSystemAudio ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                        <div className="flex-1">
                            <p className={`text-sm font-medium ${includeSystemAudio ? 'text-blue-800' : 'text-gray-700'}`}>
                                Incluir audio del sistema
                            </p>
                            <p className={`text-xs mt-0.5 ${includeSystemAudio ? 'text-blue-600' : 'text-gray-400'}`}>
                                Activa solo si usas audifonos
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={startRecording}
                            disabled={!recordingTitle.trim() || isStarting}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-red-500 hover:bg-red-600 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
                        >
                            <div className="w-3 h-3 rounded-full bg-white/90" />
                            {isStarting ? 'Iniciando...' : 'Iniciar grabacion'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );

    // ============================================
    // RENDER: Grabacion activa
    // ============================================
    const renderRecording = () => (
        <div className="meeting-panel">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="min-h-full flex flex-col"
            >
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="w-full max-w-4xl">
                        {/* Titulo y participantes */}
                        <h2 className="text-xl font-semibold text-gray-900 mb-1 text-center">{recordingTitle}</h2>
                        {recordingParticipants.length > 0 && (
                            <div className="flex items-center justify-center gap-2 mb-6">
                                <div className="flex -space-x-2">
                                    {recordingParticipants.slice(0, 5).map((p) => (
                                        <div
                                            key={p.id}
                                            className="w-7 h-7 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-gray-500"
                                            title={p.nombre}
                                        >
                                            {p.nombre.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                        </div>
                                    ))}
                                </div>
                                <span className="text-xs text-gray-400">
                                    {recordingParticipants.length} participante{recordingParticipants.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        )}
                        {recordingParticipants.length === 0 && <div className="mb-6" />}

                        {/* Timer */}
                        <div className="mb-8 text-center">
                            <RecordingTimer seconds={recordingSeconds} />
                        </div>

                        {/* Aviso de 2 horas */}
                        {recordingSeconds >= 6600 && recordingSeconds < 7200 && (
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <AlertCircle size={14} className="text-amber-500" />
                                <span className="text-sm text-amber-600 font-medium">
                                    La grabacion se detendra automaticamente en {formatDuration(7200 - recordingSeconds)}
                                </span>
                            </div>
                        )}

                        {/* Waveform */}
                        <div className="w-full max-w-md mb-8 mx-auto">
                            <Waveform isPlaying={!isPaused} color="#3b82f6" barCount={50} />
                        </div>

                        {/* Indicador de grabacion */}
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <div className={`w-2.5 h-2.5 rounded-full ${isPaused ? 'bg-yellow-400' : 'bg-red-500 recording-pulse'}`} />
                            <span className="text-sm text-gray-500">{isPaused ? 'En pausa' : 'Grabando'}</span>
                        </div>

                        {/* VU meter en vivo: confirma al usuario que entra audio real */}
                        <div className="flex flex-col items-center gap-1 mb-3">
                            <div className="w-48 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-100 ${
                                        audioLevel > 60 ? 'bg-red-400' : audioLevel > 20 ? 'bg-green-500' : audioLevel > 5 ? 'bg-green-300' : 'bg-gray-300'
                                    }`}
                                    style={{ width: `${audioLevel}%` }}
                                />
                            </div>
                            <span className="text-[10px] text-gray-400 uppercase tracking-wider">
                                {audioLevel < 5 && !isPaused ? 'Sin senal de audio' : 'Senal de audio'}
                            </span>
                        </div>

                        {/* Confirmacion de uploads — el usuario VE que esta llegando al servidor */}
                        <div className="flex items-center justify-center gap-3 mb-3 text-xs text-gray-400">
                            <span>{uploadedCount} fragmento(s) subido(s)</span>
                            {pendingUploads > 0 && (
                                <span className="text-amber-600">
                                    {pendingUploads} en cola
                                </span>
                            )}
                        </div>

                        {/* Indicador de salud: warning/error visible */}
                        {recorderHealth !== 'ok' && healthMessage && (
                            <div className={`flex items-center justify-center gap-2 mb-6 px-3 py-2 rounded-lg ${
                                recorderHealth === 'failed' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                            }`}>
                                <AlertCircle size={14} className={recorderHealth === 'failed' ? 'text-red-500' : 'text-amber-500'} />
                                <span className={`text-xs font-medium ${recorderHealth === 'failed' ? 'text-red-700' : 'text-amber-700'}`}>
                                    {healthMessage}
                                </span>
                            </div>
                        )}

                        {recorderHealth === 'ok' && <div className="mb-6" />}

                        {/* Controles */}
                        <div className="flex items-center justify-center gap-4">
                            <button
                                onClick={togglePause}
                                className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors shadow-sm"
                                title={isPaused ? 'Reanudar' : 'Pausar'}
                            >
                                {isPaused ? <Play size={20} className="text-gray-600 ml-0.5" /> : <Pause size={20} className="text-gray-600" />}
                            </button>
                            <button
                                onClick={stopRecording}
                                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg shadow-red-500/25"
                                title="Detener grabacion"
                            >
                                <Square size={20} className="text-white" fill="white" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Aviso de consentimiento */}
                <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-3">
                    <div className="max-w-lg mx-auto flex items-center gap-2.5 text-center justify-center">
                        <Shield size={14} className="text-gray-400 shrink-0" />
                        <p className="text-xs text-gray-400">
                            Centro SURA esta transcribiendo esta reunion para generar notas a partir de ella. Al grabar, confirmas el consentimiento de todas las personas presentes.
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );

    // ============================================
    // RENDER: Processing
    // ============================================
    const renderProcessing = () => (
        <div className="meeting-panel meeting-panel--centered">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="meeting-panel__content text-center"
            >
                <button onClick={backToList} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors mx-auto">
                    <ArrowLeft size={14} /> Volver al listado
                </button>
                <AiLoadingOverlay
                    inline
                    phases={[processingStep || 'Procesando...']}
                />

                <div className="w-64 h-1.5 bg-gray-100 rounded-full overflow-hidden mx-auto mb-2 mt-4">
                    <motion.div
                        className="h-full bg-blue-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${processingProgress}%` }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                    />
                </div>
                {processingProgress > 0 && (
                    <p className="text-xs text-gray-400">{Math.round(processingProgress)}%</p>
                )}

                <button
                    onClick={reprocess}
                    className="mt-8 text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
                >
                    ¿Se quedo atascada? Reintentar procesamiento
                </button>
            </motion.div>
        </div>
    );

    // ============================================
    // RENDER: Grabacion interrumpida (recuperacion)
    // ============================================
    const renderInterrupted = () => {
        const meeting = selectedMeeting;
        if (!meeting) return null;

        const chunks = interruptedInfo?.chunks_subidos;
        const sinAudio = interruptedInfo?.sin_audio || chunks === 0;
        const cargandoInfo = interruptedInfo === null;
        // Cada chunk equivale a ~15s de audio (intervalo de flush)
        const audioEstimado = chunks > 0 ? formatDuration(chunks * 15) : null;

        return (
            <div className="meeting-panel meeting-panel--centered">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="meeting-panel__content meeting-panel__content--narrow text-center"
                >
                    <div className="w-20 h-20 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-6">
                        <Mic size={32} className="text-amber-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Grabacion interrumpida</h3>
                    <p className="text-sm font-medium text-gray-700 mb-1">{meeting.titulo}</p>
                    <p className="text-xs text-gray-400 mb-6">{formatDateFull(meeting.fecha)}</p>

                    {cargandoInfo ? (
                        <div className="flex items-center justify-center gap-2 mb-8 text-sm text-gray-400">
                            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full" />
                            Verificando audio guardado...
                        </div>
                    ) : sinAudio ? (
                        <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                            No llego audio al servidor antes de la interrupcion.
                            Solo queda descartar esta grabacion.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                            La grabacion se corto, pero{' '}
                            <span className="font-medium text-gray-700">
                                {chunks != null
                                    ? `${chunks} fragmento(s)${audioEstimado ? ` (~${audioEstimado} de audio)` : ''}`
                                    : 'parte del audio'}
                            </span>{' '}
                            quedaron a salvo en el servidor. Puedes continuar grabando,
                            procesar lo que hay, o descartarla.
                        </p>
                    )}

                    <div className="flex flex-col items-center gap-3">
                        {!cargandoInfo && !sinAudio && (
                            <>
                                <button
                                    onClick={reanudarGrabacion}
                                    className="w-full max-w-xs flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
                                >
                                    <Play size={15} />
                                    Reanudar grabacion
                                </button>
                                <button
                                    onClick={procesarInterrumpida}
                                    className="w-full max-w-xs flex items-center justify-center gap-2 px-6 py-3 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-medium transition-colors"
                                >
                                    <Sparkles size={15} />
                                    Procesar lo grabado
                                </button>
                            </>
                        )}
                        <button
                            onClick={descartarInterrumpida}
                            className="w-full max-w-xs flex items-center justify-center gap-2 px-6 py-3 text-red-500 hover:bg-red-50 rounded-xl text-sm font-medium transition-colors"
                        >
                            <Trash2 size={15} />
                            Descartar grabacion
                        </button>
                        <button
                            onClick={backToList}
                            className="mt-1 flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <ArrowLeft size={14} /> Volver al listado
                        </button>
                    </div>
                </motion.div>
            </div>
        );
    };

    // ============================================
    // RENDER: Uploading final (entre stop y procesamiento backend)
    // ============================================
    const renderUploadingFinal = () => (
        <div className="meeting-panel meeting-panel--centered">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="meeting-panel__content text-center"
            >
                <div className="mb-4">
                    <RefreshCw size={32} className="text-blue-500 mx-auto animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Subiendo audio...</h3>
                <p className="text-sm text-gray-500 mb-4">
                    No cierres esta ventana. Faltan {pendingUploads} fragmento(s) por subir.
                </p>
                <p className="text-xs text-gray-400">
                    {uploadedCount} subido(s) · {pendingUploads} en cola
                </p>
            </motion.div>
        </div>
    );

    // ============================================
    // RENDER: Error
    // ============================================
    const renderError = () => (
        <div className="meeting-panel meeting-panel--centered">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="meeting-panel__content meeting-panel__content--narrow text-center"
            >
                <div className="w-20 h-20 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-6">
                    <AlertCircle size={32} className="text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Error en el procesamiento</h3>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                    {errorMessage || 'Ocurrio un error durante el procesamiento de la minuta.'}
                </p>
                <div className="flex items-center justify-center gap-3">
                    <button
                        onClick={backToList}
                        className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-sm font-medium transition-colors"
                    >
                        <ArrowLeft size={14} />
                        Volver
                    </button>
                    <button
                        onClick={reprocess}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
                    >
                        <RefreshCw size={14} />
                        Reprocesar
                    </button>
                </div>
            </motion.div>
        </div>
    );

    // ============================================
    // RENDER: Resultado estructurado
    // ============================================
    const renderResult = () => {
        const meeting = selectedMeeting;
        if (!meeting?.resultado) return null;
        const { resultado } = meeting;

        const timelineIcons = {
            tema: { icon: BookOpen, color: 'text-blue-500', bg: 'bg-blue-50' },
            decision: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
            tarea: { icon: Flag, color: 'text-amber-500', bg: 'bg-amber-50' },
            acuerdo: { icon: CheckCircle, color: 'text-purple-500', bg: 'bg-purple-50' },
        };

        const prioridadColors = {
            alta: 'bg-red-50 text-red-600 border-red-200',
            media: 'bg-amber-50 text-amber-600 border-amber-200',
            baja: 'bg-green-50 text-green-600 border-green-200',
        };

        // Render section content - new format: contenido is string, puntos is string array
        const renderSectionContent = (seccion) => (
            <div className="space-y-3">
                {seccion.contenido && (
                    <div className="space-y-3">
                        {seccion.contenido.split('\n\n').map((paragraph, pi) => (
                            <p key={pi} className="text-sm text-gray-600 leading-relaxed">
                                {renderBoldText(paragraph)}
                            </p>
                        ))}
                    </div>
                )}
                {seccion.puntos && seccion.puntos.length > 0 && (
                    <ul className="space-y-2">
                        {seccion.puntos.map((item, ii) => (
                            <li key={ii} className="flex gap-2 text-sm text-gray-600">
                                <span className="text-blue-400 mt-1 shrink-0">-</span>
                                <span className="leading-relaxed">
                                    {renderBoldText(item)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );

        // Parse resumen string into paragraphs
        const resumenParagraphs = typeof resultado.resumen === 'string'
            ? resultado.resumen.split('\n\n').filter(p => p.trim())
            : Array.isArray(resultado.resumen)
                ? resultado.resumen
                : [];

        return (
            <div className="meeting-panel meeting-panel--result">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="meeting-panel__content meeting-panel__content--result"
                >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-8">
                        <div>
                            <button onClick={backToList} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-3 transition-colors">
                                <ArrowLeft size={14} /> Todas las minutas
                            </button>
                            <h1 className="text-2xl font-semibold text-gray-900 mb-2">{meeting.titulo}</h1>
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                                <span className="flex items-center gap-1.5">
                                    <Calendar size={14} />
                                    {formatDateFull(meeting.fecha)}
                                </span>
                                {meeting.duracion_segundos > 0 && (
                                    <span className="flex items-center gap-1.5">
                                        <Clock size={14} />
                                        {formatDuration(meeting.duracion_segundos)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => deleteMeeting(meeting.id)}
                                className="p-2 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                                title="Eliminar minuta"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Tabs: Análisis IA / Transcripción */}
                    <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
                        <button
                            onClick={() => setResultTab('analisis')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                resultTab === 'analisis'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Sparkles size={14} />
                            Análisis IA
                        </button>
                        <button
                            onClick={() => setResultTab('transcripcion')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                resultTab === 'transcripcion'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Mic size={14} />
                            Transcripción y audio
                        </button>
                    </div>

                    {/* Tab: Transcripción y audio */}
                    {resultTab === 'transcripcion' && (
                        <div className="space-y-4">
                            {/* Audio player */}
                            {meeting.audio_path && (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                            <Mic size={16} className="text-blue-500" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 text-sm">Audio original</h3>
                                            <p className="text-xs text-gray-400">{formatDuration(meeting.duracion_segundos || 0)}</p>
                                        </div>
                                    </div>
                                    <AudioPlayerAutenticado minutaId={meeting.id} />
                                </div>
                            )}

                            {/* Transcripción cruda */}
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                                        <BookOpen size={16} className="text-gray-500" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 text-sm">Transcripción completa</h3>
                                    <button
                                        onClick={() => copyToClipboard(meeting.transcripcion_raw || '')}
                                        className="ml-auto p-1.5 text-gray-300 hover:text-gray-500 transition-colors"
                                        title="Copiar transcripción"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                                {meeting.transcripcion_raw ? (
                                    <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap max-h-[600px] overflow-y-auto">
                                        {meeting.transcripcion_raw}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Transcripción no disponible.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tab: Análisis IA */}
                    {resultTab === 'analisis' && <>

                    {/* Participantes */}
                    {meeting.participantes && meeting.participantes.length > 0 && (
                        <div className="flex items-center gap-3 mb-4">
                            <Users size={16} className="text-gray-400 shrink-0" />
                            <div className="flex flex-wrap gap-2">
                                {meeting.participantes.map((p) => (
                                    <span key={p.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-full text-sm text-gray-600">
                                        <User size={12} className="text-gray-400" />
                                        {p.nombre}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Banner IA */}
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3 mb-4">
                        <Sparkles size={18} className="text-violet-500 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm font-medium text-violet-800">Resumen generado con IA</p>
                            <p className="text-xs text-violet-600 mt-0.5">Este analisis fue procesado automaticamente a partir de la transcripcion del audio de la reunion.</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Resumen */}
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                            <button
                                onClick={() => toggleSection('resumen')}
                                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                        <BookOpen size={16} className="text-blue-500" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900">Resumen</h3>
                                </div>
                                <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.resumen ? 'rotate-90' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {expandedSections.resumen && (
                                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                        <div className="px-6 pb-5 space-y-3">
                                            {resumenParagraphs.map((p, i) => (
                                                <p key={i} className="text-sm text-gray-600 leading-relaxed">
                                                    {renderBoldText(typeof p === 'string' ? p : '')}
                                                </p>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Secciones detalladas */}
                        {resultado.secciones?.map((seccion, si) => (
                            <div key={si} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection(`seccion-${si}`)}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center">
                                            <BookOpen size={16} className="text-slate-500" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900">{seccion.titulo}</h3>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections[`seccion-${si}`] !== false ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections[`seccion-${si}`] !== false && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5">
                                                {renderSectionContent(seccion)}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}

                        {/* Participantes */}
                        {resultado.participantes_detalle && resultado.participantes_detalle.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection('participantes')}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                                            <Users size={16} className="text-teal-500" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900">Participantes identificados</h3>
                                        <span className="text-xs text-gray-400">{resultado.participantes_detalle.length}</span>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.participantes ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections.participantes && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5 space-y-3">
                                                {resultado.participantes_detalle.map((p, i) => (
                                                    <div key={i} className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-500 shrink-0">
                                                            {p.nombre?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <div className="flex items-center gap-2">
                                                                    <p className="text-sm font-medium text-gray-800">{p.nombre}</p>
                                                                    {p.tipo === 'externo' && (
                                                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200">Externo</span>
                                                                    )}
                                                                    {p.tipo === 'no_registrado' && (
                                                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200">No registrado</span>
                                                                    )}
                                                                    {p.sentimiento && p.sentimiento !== 'neutral' && (
                                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                                                            p.sentimiento === 'entusiasta' ? 'bg-green-50 text-green-600' :
                                                                            p.sentimiento === 'frustrado' ? 'bg-red-50 text-red-600' :
                                                                            p.sentimiento === 'cauteloso' ? 'bg-yellow-50 text-yellow-600' :
                                                                            'bg-gray-50 text-gray-500'
                                                                        }`}>{p.sentimiento}</span>
                                                                    )}
                                                                </div>
                                                                <span className="text-xs text-gray-400">{p.participacion_porcentaje}%</span>
                                                            </div>
                                                            <p className="text-xs text-gray-400 mb-1.5">{p.rol_detectado}</p>
                                                            {p.postura && (
                                                                <p className="text-xs text-gray-500 italic mb-1.5">{p.postura}</p>
                                                            )}
                                                            {p.temas_principales && p.temas_principales.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mb-1.5">
                                                                    {p.temas_principales.map((tema, ti) => (
                                                                        <span key={ti} className="text-[10px] px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded">
                                                                            {tema}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                <div className="h-full bg-teal-400 rounded-full bar-animate" style={{ width: `${p.participacion_porcentaje}%` }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Linea de tiempo */}
                        {resultado.timeline && resultado.timeline.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection('timeline')}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                                            <Clock size={16} className="text-indigo-500" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900">Linea de tiempo</h3>
                                        <span className="text-xs text-gray-400">{resultado.timeline.length} eventos</span>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.timeline ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections.timeline && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5">
                                                <div className="space-y-0">
                                                    {resultado.timeline.map((event, i) => {
                                                        const config = timelineIcons[event.tipo] || timelineIcons.tema;
                                                        const Icon = config.icon;
                                                        return (
                                                            <div key={i} className="flex gap-4 relative">
                                                                {i < resultado.timeline.length - 1 && (
                                                                    <div className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-100" />
                                                                )}
                                                                <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center shrink-0 z-10`}>
                                                                    <Icon size={14} className={config.color} />
                                                                </div>
                                                                <div className="flex-1 pb-4">
                                                                    <div className="flex items-center gap-2 mb-0.5">
                                                                        <span className="font-mono text-xs text-gray-400">{event.tiempo}</span>
                                                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${config.bg} ${config.color}`}>
                                                                            {event.tipo === 'tema' ? 'Tema' : event.tipo === 'decision' ? 'Decision' : event.tipo === 'tarea' ? 'Tarea' : 'Acuerdo'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-700">{event.descripcion}</p>
                                                                    {event.speaker && (
                                                                        <span className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                                                                            <User size={10} /> {event.speaker}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Tareas y proximos pasos */}
                        {resultado.tareas && resultado.tareas.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection('tareas')}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                                            <Flag size={16} className="text-amber-500" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900">Proximos pasos y acciones</h3>
                                        <span className="text-xs text-gray-400">{resultado.tareas.length}</span>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.tareas ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections.tareas && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5 space-y-3">
                                                {resultado.tareas.map((tarea, i) => (
                                                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg group">
                                                        {/* Checkbox visual */}
                                                        <div className="mt-0.5 shrink-0">
                                                            <div className="w-4.5 h-4.5 rounded border-2 border-gray-300 group-hover:border-blue-400 transition-colors" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-gray-800 mb-1 leading-relaxed">{tarea.descripcion}</p>
                                                            <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                                                                <span className="flex items-center gap-1">
                                                                    <User size={11} />
                                                                    {tarea.responsable}
                                                                </span>
                                                                {tarea.asignado_por && (
                                                                    <span className="text-gray-300">asignada por {tarea.asignado_por}</span>
                                                                )}
                                                                {tarea.fecha_limite && (
                                                                    <span className="flex items-center gap-1">
                                                                        <Calendar size={11} />
                                                                        {formatDate(tarea.fecha_limite)}
                                                                    </span>
                                                                )}
                                                                {tarea.prioridad && (
                                                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${prioridadColors[tarea.prioridad] || prioridadColors.media}`}>
                                                                        {tarea.prioridad.charAt(0).toUpperCase() + tarea.prioridad.slice(1)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button
                                                                onClick={() => {
                                                                    // Normalizar prioridad de minuta a tarea
                                                                    const prioridadValida = ['urgente', 'alta', 'media', 'baja'].includes(tarea.prioridad)
                                                                        ? tarea.prioridad
                                                                        : 'ninguna';
                                                                    // Intentar parsear fecha
                                                                    let fechaParsed = null;
                                                                    if (tarea.fecha_limite) {
                                                                        const d = new Date(tarea.fecha_limite);
                                                                        if (!isNaN(d.getTime())) {
                                                                            fechaParsed = d.toISOString().slice(0, 10);
                                                                        }
                                                                    }
                                                                    // Construir descripcion enriquecida
                                                                    const partes = [];
                                                                    if (tarea.responsable) partes.push(`Responsable según minuta: ${tarea.responsable}`);
                                                                    if (tarea.asignado_por) partes.push(`Asignada por: ${tarea.asignado_por}`);
                                                                    setConvertPrefill({
                                                                        titulo: tarea.descripcion || '',
                                                                        descripcion: partes.join('\n'),
                                                                        prioridad: prioridadValida,
                                                                        fecha: fechaParsed,
                                                                    });
                                                                    setConvertModalOpen(true);
                                                                }}
                                                                className="p-1.5 text-gray-300 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                                                title="Convertir en tarea del módulo Proyectos"
                                                            >
                                                                <KanbanSquare size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => copyToClipboard(tarea.descripcion)}
                                                                className="p-1.5 text-gray-300 hover:text-gray-500 transition-colors"
                                                                title="Copiar"
                                                            >
                                                                <Copy size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Citas relevantes */}
                        {resultado.citas_relevantes?.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection('citas')}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                                            <Sparkles size={16} className="text-violet-500" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900">Citas relevantes</h3>
                                        <span className="text-xs text-gray-400">{resultado.citas_relevantes.length}</span>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.citas ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections.citas && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5 space-y-3">
                                                {resultado.citas_relevantes.map((cita, i) => (
                                                    <div key={i} className="border-l-2 border-violet-200 pl-4 py-1">
                                                        <p className="text-sm text-gray-700 italic">"{cita.texto}"</p>
                                                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                                                            {cita.speaker && (
                                                                <span className="flex items-center gap-1 font-medium text-violet-600">
                                                                    <User size={10} /> {cita.speaker}
                                                                </span>
                                                            )}
                                                            {cita.contexto && <span>{cita.contexto}</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Datos y cifras mencionadas */}
                        {resultado.datos_mencionados?.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection('datos')}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                                            <span className="text-emerald-500 font-bold text-xs">#</span>
                                        </div>
                                        <h3 className="font-semibold text-gray-900">Datos y cifras</h3>
                                        <span className="text-xs text-gray-400">{resultado.datos_mencionados.length}</span>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.datos ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections.datos && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5 space-y-2">
                                                {resultado.datos_mencionados.map((d, i) => (
                                                    <div key={i} className="flex items-start gap-2 text-sm">
                                                        <span className="text-emerald-500 font-medium shrink-0">-</span>
                                                        <div>
                                                            <span className="font-medium text-gray-800">{d.dato}</span>
                                                            {d.contexto && <span className="text-gray-400"> — {d.contexto}</span>}
                                                            {d.mencionado_por && <span className="text-gray-300 text-xs ml-1">({d.mencionado_por})</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Temas pendientes */}
                        {resultado.temas_pendientes?.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => toggleSection('pendientes')}
                                    className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                                            <AlertCircle size={16} className="text-orange-500" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900">Temas pendientes</h3>
                                        <span className="text-xs text-gray-400">{resultado.temas_pendientes.length}</span>
                                    </div>
                                    <ChevronRight size={16} className={`text-gray-400 transition-transform ${expandedSections.pendientes ? 'rotate-90' : ''}`} />
                                </button>
                                <AnimatePresence>
                                    {expandedSections.pendientes && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-6 pb-5 space-y-2">
                                                {resultado.temas_pendientes.map((tema, i) => (
                                                    <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                                        <span className="text-orange-400 mt-0.5 shrink-0">!</span>
                                                        <span>{tema}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Efectividad de la reunión */}
                        {resultado.efectividad && (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className={`text-2xl font-bold ${
                                        resultado.efectividad.puntuacion >= 7 ? 'text-green-600' :
                                        resultado.efectividad.puntuacion >= 4 ? 'text-amber-600' :
                                        'text-red-600'
                                    }`}>
                                        {resultado.efectividad.puntuacion}/10
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800">Efectividad de la reunión</p>
                                        <p className="text-xs text-gray-500">{resultado.efectividad.justificacion}</p>
                                    </div>
                                </div>
                                {resultado.efectividad.sugerencias?.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-blue-100">
                                        <p className="text-[10px] uppercase font-semibold text-blue-400 mb-1">Sugerencias</p>
                                        {resultado.efectividad.sugerencias.map((s, i) => (
                                            <p key={i} className="text-xs text-gray-600">- {s}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Metadata / tono general */}
                        {resultado.metadata && (
                            <div className="flex items-center gap-3 px-1 text-xs text-gray-400">
                                {resultado.metadata.tono_general && (
                                    <span>Tono: <span className="font-medium text-gray-500 capitalize">{resultado.metadata.tono_general}</span></span>
                                )}
                                {resultado.metadata.total_decisiones > 0 && (
                                    <span>{resultado.metadata.total_decisiones} decisiones</span>
                                )}
                                {resultado.metadata.total_tareas > 0 && (
                                    <span>{resultado.metadata.total_tareas} tareas</span>
                                )}
                                {resultado.metadata.confianza_speakers && (
                                    <span>Speakers: <span className="font-medium text-gray-500 capitalize">{resultado.metadata.confianza_speakers}</span></span>
                                )}
                            </div>
                        )}
                    </div>
                    </>}
                </motion.div>
            </div>
        );
    };

    // ============================================
    // RENDER PRINCIPAL
    // ============================================
    return (
        <div className="meeting-view-container">
            {phase !== 'recording' && renderSidebar()}

            <div className="meeting-main">
                <AnimatePresence mode="wait">
                    {phase === 'idle' && !selectedMeeting && renderEmpty()}
                    {phase === 'setup' && renderSetup()}
                    {phase === 'recording' && renderRecording()}
                    {phase === 'uploading_final' && renderUploadingFinal()}
                    {phase === 'processing' && renderProcessing()}
                    {phase === 'interrupted' && selectedMeeting && renderInterrupted()}
                    {phase === 'error' && renderError()}
                    {(phase === 'done' && selectedMeeting) && renderResult()}
                </AnimatePresence>
            </div>

            <CrearTareaModal
                isOpen={convertModalOpen}
                onClose={() => setConvertModalOpen(false)}
                tituloPrefill={convertPrefill.titulo}
                descripcionPrefill={convertPrefill.descripcion}
                prioridadPrefill={convertPrefill.prioridad}
                fechaPrefill={convertPrefill.fecha}
                entidadTipo={selectedMeeting ? 'minuta' : null}
                entidadId={selectedMeeting?.id}
                contextLabel={selectedMeeting ? `Desde minuta: ${selectedMeeting.titulo}` : null}
                navigateAfter={false}
            />
        </div>
    );
};

export default MeetingRecordingView;
